import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Table,
  Select,
  message,
  Input,
  Checkbox,
  Typography,
  Row,
} from "antd";
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import { CloseCircleOutlined } from "@ant-design/icons";
import GenericModal from "../buttons/genericModal";
import EnhancedFDICDisclosure from "./disclosures/enhancedFDIC";
import { linkBankAccountGenericMulti } from '../../helpers/newBankAccount'
import { createMultiPostObject2 } from '../../helpers/linkBankAccount';
import SelectBankAccountTag from "../specific/customTags/selectBankAccountTag";
import SelectBankAccountPurpose from "../specific/customTags/selectBankAccountPurpose";

const { Option } = Select;
const { Text } = Typography;

const NewBankAccountForEntity = (props) => {

  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [nextIndex, setNextIndex] = useState(0);

  const [requestData, setRequestData] = useState({})

  const formRef = useRef();

  useEffect(() => {
    if (!visible) {
      setSubmitting(false);
      setTableData([]);
      setNextIndex(0);
    }
  }, [visible]);

  const columns = [
    {
      title: "Hierarchy Tags",
      width: "35%",
      dataIndex: "tags",
    },
    {
      title: "Purpose",
      width: "20%",
      dataIndex: "purpose",
      
    },
    {
      title: "Name",
      dataIndex: "description",
      width: "40%",
    },
    {
      title: "",
      dataIndex: "deleteRow",
      width: "5%",
    },
  ];

  const onFormSubmitModal = (values) => {
    if (values.agreement === undefined || values.agreement === false) {
      message.info('Please agree to the terms before submitting.');
      return;
    }
    setSubmitting(true);
    linkBankAccountGenericMulti(
      createMultiPostObject2(requestData, 'UN', props.entityId),
      Object.keys(requestData), 
      (responseData) => {
        setSubmitting(false);
        setVisible(false);
        props.addNewBankAccounts(responseData);
      },
      () => {
        setSubmitting(false);
      },
    );
  };

  const createNewRow = (index) => {
    const newRow = {
      key: index,
      tags:
        <>
          <Form.Item
            name="tags"
            style={{ display: "flex", flexDirection: "row" }}
            rules={[
              {
                required: true,
                message: "Please select account placement",
              },
            ]}
          >
            <SelectBankAccountTag
              loadInComponent={true}
              loading={false}
              placeholder={"Select tags to associated with this account"}
              onChange={(e) => setRequestData(prev => {
                prev[index]['genericTags'] = e
                return prev
              })}
              includeUncategorized={false}
              multiSelect={true}
              required={true}
            />
          </Form.Item>
        </>,
      purpose:
        <>
          <Form.Item
            name="purpose"
            style={{ display: "flex", flexDirection: "row" }}
            rules={[
              {
                required: true,
                message: "Please select account purpose",
              },
            ]}
          >
            <SelectBankAccountPurpose
              placeholder={"Select purpose of account"}
              onChange={(e) => setRequestData(prev => {
                prev[index]['purpose'] = e
                return prev
              })}
            />
          </Form.Item>
        </>,
      description: <>
        {/* Account Nickname */}
        <Form.Item
          name={"description" + index}
          rules={[
            {
              required: true,
              message: "Please input an account name",
            },
          ]}
        >
          <Input 
              onChange={(e) => setRequestData(prev => {
                prev[index]['name'] = e.target.value
                return prev
              })} />
        </Form.Item>
      </>,
      deleteRow: <>
        <CloseCircleOutlined
          style={{ fontSize: "18px", color: "#787676" }}
          onClick={() => removeRow(index)}
        />
      </>
    };

    return [newRow, index + 1];
  }

  const addNewRow = () => {
    return new Promise((resolve) => {
      setNextIndex((prevState) => {
        resolve(prevState + 1);
        return prevState + 1;
      });
    }).then((nextIndex) => {
      let res = createNewRow(nextIndex);
      setTableData(prevState => [...prevState, res[0]]);
      setRequestData({...requestData, [nextIndex]: {
        'genericTags': [],
        'purpose': undefined,
        'name': ''
      }})
    });
  };

  const removeRow = (index) => {
    setTableData((prevState) => {
      const filteredRows = prevState.filter(
        (row) => row.key !== index
      );
      return filteredRows
    })
    let newRequestData = requestData
    delete newRequestData[index]
    setRequestData(newRequestData)
    // nothing to next index
    return;
  }

  const stepContent = [
    <Form
      ref={formRef}
      layout="vertical"
      name="basic"
      onFinish={onFormSubmitModal}
      autoComplete="off"
    >
      <LoadingButton
        disableElevation fullWidth size="small" type="submit" variant="outlined" color="secondary"
        loadingPosition="start"
        onClick={addNewRow}
        style={{ marginBottom: "16px", width: '100px' }}
        sx={{ fontSize: 12, textTransform: 'none' }}
      >
        Add a row
      </LoadingButton>
      <Table
        dataSource={tableData}
        columns={columns}
        pagination={false}
      />

      {/* Accept terms of bank account if internal */}
      <>
        <Form.Item name="agreement" label="" valuePropName="checked" style={{ marginTop: 10 }}>
          <Checkbox>
            <Row>
              <Text>
                I agree to the{" "}
                <a href="https://www.arpari.com/legal/deposit-account-agreement" target="_blank">
                  Arpari Business Deposit Account
                  Agreement with Thread Bank.
                </a>
              </Text>
            </Row>
            <Row>
              <Text type="secondary" style={{ fontStyle: 'italic' }}>
                <EnhancedFDICDisclosure />
              </Text>
            </Row>
          </Checkbox>
        </Form.Item>
      </>

      {/* Submit */}
      <Form.Item>
        <LoadingButton
          disableElevation fullWidth size="small" type="submit" variant="contained" color="primary"
          style={{ marginBottom: "16px" }}
          sx={{ fontSize: 12, width: '100%', textTransform: 'none' }}
          htmlType="submit" disabled={!tableData.length}>
          Submit
        </LoadingButton>
      </Form.Item>
    </Form>,
  ];

  const modalBody = <>
    <div>
      {stepContent}
    </div>
  </>;

  const modalContent = (
    <div>
      <GenericModal
        size="lg"
        visible={visible}
        handleCancelModal={() => setVisible(false)}
        showModal={() => setVisible(true)}
        button={
          <Button
            disableElevation fullWidth size="small" type="submit"
            variant={props.buttonVariant || "outlined"}
            color={props.buttonColor || "secondary"}
            sx={{ fontSize: 14, width: '200px', textTransform: 'none' }}>
            Open Bank Account(s)
          </Button>
        }
        modalTitle={props.title}
        modalBody={modalBody}
      />
    </div>
  );

  return modalContent
};

export default NewBankAccountForEntity;  